import {EnergyCommunity, EntitySubType, EntityType} from 'smag-commons/cfg/hierarchy';
import {QuantityType} from 'smag-commons/dto/msg';
import {EnergyCommunityPod} from 'smag-commons/dto/api';
import {HierarchyEntity} from './hierarchy.service';

export interface EnergyCommunityNetwork {
    root: HierarchyEntity; // NOTE: network's id is `root.id`
    rootEntity: EnergyCommunity; // details for entity `root`
    entities: { [id: string]: HierarchyEntity }; // used to speed up membership checks
    pods: { [id: string]: EnergyCommunityPod };
}

/**
 * Returns a user-friendly name for the given {@link Entity entity}.
 *
 * @param entity The entity (only its type and sub-type matter)
 * @return string Entity's name
 */
export function entityName(entity: { type: EntityType, sub_type?: EntitySubType }): string {
    switch (entity.type) {
        case EntityType.ROOT_NODE:
            return 'Root node';
        case EntityType.ENERGY_COMMUNITY:
            switch (entity.sub_type) {
                case EntitySubType.RENEWABLE_ENERGY_COMMUNITY:
                    return 'Renewable Energy Community';
                case EntitySubType.COLLECTIVE_SELF_CONSUMPTION_GROUP:
                    return 'Collective self-consumption group';
                default:
                    return 'Energy Community';
            }
        case EntityType.POD:
            return 'Exchange POD';
        case EntityType.PLANT:
            switch (entity.sub_type) {
                case EntitySubType.PV_PLANT:
                    return 'Photovoltaic plant';
                case EntitySubType.WIND_PLANT:
                    return 'Wind plant';
                default:
                    return 'Plant';
            }
        case EntityType.ACCUMULATOR:
            return 'Accumulator';
        default:
            return null;
    }
}

/**
 * Returns the correct device parameter's value with the given prefix and suffix.
 *
 * @param prefix Prefix of the device parameter
 * @param suffix Suffix of the device parameter
 * @return string The value of the device parameter
 */
export function mkDeviceParamValue(prefix: string, suffix: string): string {
    return `${prefix}${!prefix.endsWith('_') ? '_' : ''}${suffix}`;
}

/**
 * Returns the "standard" unit-of-measure used by the system for the given {@link QuantityType quantity type}.
 * This UOM is used within plannings and forecasts.
 *
 * @param qt Quantity type
 * @return string Quantity unit-of-measure
 */
export function mkQuantityUom(qt: QuantityType): string {
    switch (qt) {
        case QuantityType.ACTIVE_POWER:
            return 'kW';
        case QuantityType.REACTIVE_POWER:
            return 'kVAR';
        case QuantityType.THERMAL_POWER:
            return 'kW';
        case QuantityType.INPUT_THERMAL_POWER:
            return 'kW';
        case QuantityType.STATE_OF_CHARGE:
            return '%';
        case QuantityType.VOLTAGE:
            return 'V';
        case QuantityType.PHASE_ANGLE:
            return null;
        case QuantityType.INVERSE_ACTIVE_POWER:
            return 'kW';
        case QuantityType.INVERSE_REACTIVE_POWER:
            return 'kVAR';
        case QuantityType.CURTAILED_POWER:
            return 'kW';
        case QuantityType.SHARED_POWER:
        case QuantityType.GENERATED_POWER:
        case QuantityType.CONSUMED_POWER:
        case QuantityType.INJECTED_POWER:
        case QuantityType.ABSORBED_POWER:
        case QuantityType.STORAGE_POWER:
            return 'kW';
        default:
            return null;
    }
}

/**
 * Returns the proper multiplicative coefficient to be applied to planning/forecast data of
 * the given {@link QuantityType type}.
 *
 * @param qt Quantity type
 * @return number Coefficient (may well be 1)
 */
export function mkQuantityCoefficient(qt: QuantityType): number {
    if (qt === QuantityType.STATE_OF_CHARGE) {
        return 100; // SoC expressed as [0, 1] but displayed as [0%, 100%]
    }
    return 1;
}
